// ProductivityReport — RP-5 (2026-05-11).
//
// Hierarchical hours report per Kuhrt's 2026-05-11 spec walkthrough.
// Self-contained: owns filters, fetch, and outline-render.
//
// Spec recap:
//   - Single metric = hours.  Split Billable / Non-billable / Total at
//     every subtotal break + grand total.  Source = time_entries.is_billable
//     (productivity lens — distinct from RP-2's invoice-status lens).
//   - Three modes: Self (default for everyone), One-of-N (cap-gated picker),
//     All-at-once (cap-gated, Employee added as top-level group-by).
//   - Cap = Cap.VIEW_TEAM_REPORTS (Manager + Exec + Ownership + Admin).
//   - Dimensions = Year / Month / Week / Day / Insured / Claim.  User picks
//     subset AND order.  Default order = Year > Month > Week > Day >
//     Insured > Claim.  (Level id stays "client" for API stability — relabel
//     was terminology-only per WORKBENCH-INSURED-LIFT 2026-05-11.)
//   - Date range = default YTD, custom override.
//   - Dense day/week fill DEFERRED to RP-5-FOLLOWUP-DENSE (v1 sparse).

import React, { useState, useEffect, useMemo } from "react";
import { apiFetch, can, CLAIM_TYPES } from "../api";

const ALL_LEVELS = [
  { id: "employee", label: "Employee", allModeOnly: true },
  { id: "year",     label: "Year" },
  { id: "month",    label: "Month" },
  { id: "week",     label: "Week" },
  { id: "day",      label: "Day" },
  { id: "client",   label: "Insured" },
  { id: "claim",    label: "Claim #" },
];

// Default landing view per Kuhrt 2026-05-11: just Year + Month checked.
// Other levels (week / day / insured / claim) appear unchecked in the
// reorder UI; user toggles them on as needed.  Keeps the initial render
// readable and fast for the common case (annual / monthly review).
const DEFAULT_ORDER = ["year", "month"];
const ALL_AVAILABLE_LEVEL_IDS = ["year", "month", "week", "day", "client", "claim"];

const todayISO = () => new Date().toISOString().slice(0, 10);
const ytdStartISO = () => `${new Date().getFullYear()}-01-01`;

export default function ProductivityReport({ initialViewState, onViewStateChange } = {}) {
  const canPickAdjuster = can("view_team_reports");

  // REPORTS-UX-1: seed from a restored saved/pinned view (initialViewState) so a
  // pin/recent reopens the exact configuration; otherwise use the defaults.
  const iv = initialViewState || {};
  const [mode,      setMode]      = useState(iv.mode ?? "self");          // self | one | all
  const [userId,    setUserId]    = useState(iv.userId ?? null);         // for mode=one
  const [startDate, setStartDate] = useState(iv.startDate ?? ytdStartISO());
  const [endDate,   setEndDate]   = useState(iv.endDate ?? todayISO());
  const [claimType, setClaimType] = useState(iv.claimType ?? "");        // "" = All (default)

  // Selected levels in chosen order.  In "self"/"one" mode, "employee" is
  // automatically excluded.  In "all" mode, "employee" is auto-prepended
  // unless user explicitly picks a different order.
  const [includedIds, setIncludedIds] = useState(DEFAULT_ORDER);

  const [adjusterList, setAdjusterList] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({});   // row index → open (click-to-expand entries)

  // REPORTS-UX-1: emit serialized view state up so it can be pinned/restored.
  useEffect(() => {
    onViewStateChange && onViewStateChange({ mode, userId, startDate, endDate, claimType });
    /* eslint-disable-next-line */
  }, [mode, userId, startDate, endDate, claimType]);

  // Lazy-load full employee list when user enters one/all mode for the
  // first time.  Productivity covers EVERY active employee (accountants,
  // support, executives all log time), so we hit /users/active-for-reports
  // which returns the full active-user universe — NOT
  // /users/with-claim-assignments which is filtered to lead/staff users
  // only (correct for OCC/WIP/Payment History where adjusters are the
  // only relevant filter axis).
  useEffect(() => {
    if (!canPickAdjuster) return;
    if (mode === "self") return;
    if (adjusterList !== null) return;
    apiFetch("/users/active-for-reports")
      .then(setAdjusterList)
      .catch(e => { console.error(e); setAdjusterList([]); });
  }, [mode, canPickAdjuster, adjusterList]);

  // When switching to "all" mode, ensure employee is in the included list
  // and at the front.  When switching away, remove it.
  useEffect(() => {
    setIncludedIds(prev => {
      const hasEmp = prev.includes("employee");
      if (mode === "all" && !hasEmp) return ["employee", ...prev];
      if (mode !== "all" &&  hasEmp) return prev.filter(id => id !== "employee");
      return prev;
    });
  }, [mode]);

  const toggleLevel = (id) => {
    setIncludedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };
  const moveLevel = (id, dir) => {
    setIncludedIds(prev => {
      const i = prev.indexOf(id);
      if (i < 0) return prev;
      const j = i + dir;
      if (j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };

  const run = async () => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams();
      params.set("mode", mode);
      if (mode === "one" && userId) params.set("user_id", String(userId));
      if (startDate) params.set("start_date", startDate);
      if (endDate)   params.set("end_date",   endDate);
      if (claimType) params.set("claim_type", claimType);
      if (includedIds.length) params.set("group_by", includedIds.join(","));
      const data = await apiFetch(`/reports/productivity-by-employee?${params.toString()}`);
      setReport(data);
      setExpanded({});   // row indices change with a new grouping/run
    } catch (e) {
      setError(String(e.message || e));
      setReport(null);
    } finally {
      setLoading(false);
    }
  };

  // Run once on mount with defaults.
  useEffect(() => { run(); /* eslint-disable-next-line */ }, []);

  const visibleLevels = useMemo(() =>
    ALL_LEVELS.filter(l => !l.allModeOnly || mode === "all"),
    [mode]
  );

  return (
    <div style={{display:"flex",flexDirection:"column",gap:12}}>
      {/* === FILTER STRIP === */}
      <div style={{display:"flex",flexWrap:"wrap",gap:10,alignItems:"center",padding:"10px 12px",background:"#f7f7f9",border:"1px solid var(--border)",borderRadius:4}}>
        {/* Mode pill */}
        <span style={{display:"inline-flex",border:"1px solid var(--border)",borderRadius:4,overflow:"hidden",background:"white"}}>
          {[
            {v:"self", label:"Self",          visible:true},
            {v:"one",  label:"By Employee",   visible:canPickAdjuster},
            {v:"all",  label:"All-at-once",   visible:canPickAdjuster},
          ].filter(m=>m.visible).map(m => (
            <button key={m.v} onClick={()=>setMode(m.v)}
                    style={{padding:"6px 12px",border:"none",fontSize:12,fontWeight:500,
                            background:mode===m.v?"var(--navy)":"white",
                            color:mode===m.v?"white":"var(--text2)",cursor:"pointer"}}>
              {m.label}
            </button>
          ))}
        </span>

        {/* Employee picker (one mode only) */}
        {mode === "one" && (
          <select value={userId || ""} onChange={e=>setUserId(e.target.value ? Number(e.target.value) : null)}
                  style={{padding:"6px 8px",border:"1px solid var(--border)",borderRadius:4,background:"white",minWidth:220}}>
            <option value="">Pick employee…</option>
            {adjusterList === null
              ? <option disabled>Loading…</option>
              : adjusterList.map(u => (
                  <option key={u.id} value={u.id}>{u.full_name}</option>
                ))}
          </select>
        )}

        {/* Date range */}
        <span style={{display:"inline-flex",alignItems:"center",gap:6}}>
          <input type="date" value={startDate} onChange={e=>setStartDate(e.target.value)}
                 style={{padding:"6px 8px",border:"1px solid var(--border)",borderRadius:4}}/>
          <span style={{fontSize:12,color:"var(--text3)"}}>to</span>
          <input type="date" value={endDate} onChange={e=>setEndDate(e.target.value)}
                 style={{padding:"6px 8px",border:"1px solid var(--border)",borderRadius:4}}/>
        </span>

        {/* Claim Type filter — default All; per Kuhrt 2026-05-11 (e.g.,
            "how much of an employee's time is on H&M claims"). */}
        <select value={claimType} onChange={e=>setClaimType(e.target.value)}
                style={{padding:"6px 8px",border:"1px solid var(--border)",borderRadius:4,background:"white"}}
                title="Claim type filter">
          <option value="">All claim types</option>
          {CLAIM_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>

        <button className="btn btn-blue" onClick={run} disabled={loading || (mode==="one" && !userId)}>
          {loading ? "Running…" : "Run Report"}
        </button>

        {report?.meta && (
          <span style={{marginLeft:"auto",fontSize:11,color:"var(--text3)"}}>
            {report.meta.total_records} entries · {report.rows.length} outline rows
            {report.meta.entries_attached === false && " · too many to expand — narrow the range"}
            {report.meta.entries_attached !== false && report.rows.length > 1 && " · click a row to see its entries"}
          </span>
        )}
      </div>

      {/* === GROUP-BY REORDER === */}
      <div style={{padding:"10px 12px",background:"#f7f7f9",border:"1px solid var(--border)",borderRadius:4}}>
        <div style={{fontSize:11,fontWeight:600,color:"var(--text3)",textTransform:"uppercase",letterSpacing:".06em",marginBottom:8}}>
          Group by (in order)
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:4}}>
          {/* Render in current included order, then any uninclined levels */}
          {[...includedIds, ...visibleLevels.map(l=>l.id).filter(id => !includedIds.includes(id))].map((id, idx) => {
            const lvl = ALL_LEVELS.find(l => l.id === id);
            if (!lvl) return null;
            if (lvl.allModeOnly && mode !== "all") return null;
            const inIdx = includedIds.indexOf(id);
            const isIncluded = inIdx >= 0;
            return (
              <div key={id} style={{display:"flex",alignItems:"center",gap:8,fontSize:12}}>
                <input type="checkbox" checked={isIncluded} onChange={()=>toggleLevel(id)}
                       disabled={lvl.allModeOnly && mode === "all"} /* employee always on in all-mode */ />
                <span style={{minWidth:120,color:isIncluded?"var(--text)":"var(--text3)"}}>
                  {isIncluded ? `${inIdx+1}. ${lvl.label}` : lvl.label}
                </span>
                {isIncluded && (
                  <>
                    <button onClick={()=>moveLevel(id,-1)} disabled={inIdx<=0}
                            style={{padding:"2px 6px",fontSize:11,border:"1px solid var(--border)",background:"white",borderRadius:3,cursor:inIdx<=0?"default":"pointer",opacity:inIdx<=0?0.4:1}}>↑</button>
                    <button onClick={()=>moveLevel(id, 1)} disabled={inIdx>=includedIds.length-1}
                            style={{padding:"2px 6px",fontSize:11,border:"1px solid var(--border)",background:"white",borderRadius:3,cursor:inIdx>=includedIds.length-1?"default":"pointer",opacity:inIdx>=includedIds.length-1?0.4:1}}>↓</button>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* === OUTPUT === */}
      {error && (
        <div style={{padding:"10px 12px",background:"#fef2f2",border:"1px solid #fecaca",borderRadius:4,color:"#991b1b",fontSize:13}}>
          {error}
        </div>
      )}
      {!error && report && report.meta?.entries_attached === false && (
        <div style={{padding:"8px 12px",background:"#fffbeb",border:"1px solid #fde68a",borderRadius:4,color:"#92400e",fontSize:12}}>
          This pull has {report.meta.total_records.toLocaleString()} entries — too many to expand inline. Narrow the date range (or pick one employee) to click into individual time entries.
        </div>
      )}
      {!error && report && (
        <table style={{width:"100%",borderCollapse:"collapse",fontSize:13,fontFamily:"var(--font-sans)"}}>
          <thead>
            <tr style={{borderBottom:"2px solid var(--border)"}}>
              <th style={{textAlign:"left",padding:"8px 10px"}}>Group</th>
              <th style={{textAlign:"right",padding:"8px 10px",width:110}}>Billable</th>
              <th style={{textAlign:"right",padding:"8px 10px",width:110}}>Non-billable</th>
              <th style={{textAlign:"right",padding:"8px 10px",width:110}}>Total</th>
            </tr>
          </thead>
          <tbody>
            {report.rows.map((r, i) => {
              // Leaf rows carry their individual entries — click to expand the dropdown.
              const expandable = Array.isArray(r.entries) && r.entries.length > 0;
              const isOpen = !!expanded[i];
              return (
                <React.Fragment key={i}>
                  <tr
                    onClick={expandable ? () => setExpanded(p => ({ ...p, [i]: !p[i] })) : undefined}
                    style={{
                      borderBottom:"1px solid #eef0f4",
                      background: r.indent_level === 0 ? "#eef0f4" : (r.is_total ? "#f7f7f9" : (isOpen ? "#eef6ff" : "transparent")),
                      fontWeight: r.indent_level === 0 ? 700 : (r.is_total ? 600 : 400),
                      cursor: expandable ? "pointer" : "default",
                    }}>
                    <td style={{padding:"6px 10px",paddingLeft:`${10 + r.indent_level * 18}px`}}>
                      {expandable && <span style={{display:"inline-block",width:14,color:"var(--text3)"}}>{isOpen ? "▾" : "▸"}</span>}
                      {r.label}
                      {expandable && <span style={{marginLeft:6,fontSize:11,color:"var(--text3)"}}>({r.entries.length} {r.entries.length === 1 ? "entry" : "entries"})</span>}
                    </td>
                    <td style={{textAlign:"right",padding:"6px 10px",fontVariantNumeric:"tabular-nums",fontFamily:"JetBrains Mono, monospace"}}>
                      {r.billable_hours.toFixed(2)}
                    </td>
                    <td style={{textAlign:"right",padding:"6px 10px",fontVariantNumeric:"tabular-nums",fontFamily:"JetBrains Mono, monospace",color:r.non_billable_hours>0?"var(--text)":"var(--text3)"}}>
                      {r.non_billable_hours.toFixed(2)}
                    </td>
                    <td style={{textAlign:"right",padding:"6px 10px",fontVariantNumeric:"tabular-nums",fontFamily:"JetBrains Mono, monospace",fontWeight:r.is_total?700:500}}>
                      {r.total_hours.toFixed(2)}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr>
                      <td colSpan={4} style={{padding:0,background:"#f9fafb",borderBottom:"1px solid #e5e7eb"}}>
                        <EntriesDropdown entries={r.entries} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {report.rows.length === 0 && (
              <tr><td colSpan={4} style={{padding:"20px 10px",textAlign:"center",color:"var(--text3)",fontSize:12,fontStyle:"italic"}}>
                No time entries in the selected range.
              </td></tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}

// Click-to-expand list of the individual time entries that roll up into a leaf row.
function EntriesDropdown({ entries }) {
  const th = { textAlign:"left", padding:"5px 10px", fontSize:10, color:"var(--text3)", textTransform:"uppercase", letterSpacing:".04em", fontWeight:600 };
  const td = { padding:"5px 10px", fontSize:12, borderTop:"1px solid #eef0f4", verticalAlign:"top" };
  const num = { ...td, textAlign:"right", fontVariantNumeric:"tabular-nums", fontFamily:"JetBrains Mono, monospace" };
  const fmtDate = (iso) => { if (!iso) return "—"; const d = new Date(iso + "T00:00:00"); return d.toLocaleDateString("en-US", { month:"short", day:"numeric", year:"numeric" }); };
  return (
    <table style={{ width:"100%", borderCollapse:"collapse", margin:"2px 0 6px" }}>
      <thead><tr>
        <th style={{ ...th, paddingLeft:36, width:120 }}>Date</th>
        <th style={{ ...th, width:150 }}>Claim</th>
        <th style={th}>Description</th>
        <th style={{ ...th, textAlign:"center", width:70 }}>Type</th>
        <th style={{ ...th, textAlign:"right", width:70 }}>Hours</th>
      </tr></thead>
      <tbody>
        {entries.map((e, j) => (
          <tr key={j}>
            <td style={{ ...td, paddingLeft:36, whiteSpace:"nowrap", color:"var(--text2)" }}>{fmtDate(e.date)}</td>
            <td style={{ ...td, whiteSpace:"nowrap" }}><span className="mono" style={{ color:"var(--blue)" }}>{e.claim_number || "—"}</span></td>
            <td style={{ ...td, color:"var(--text2)", lineHeight:1.4 }}>{e.description || <span style={{ color:"var(--text3)" }}>—</span>}</td>
            <td style={{ ...td, textAlign:"center" }}>
              <span className="badge" style={{ fontSize:9, background: e.is_billable ? "#dcfce7" : "#f1f5f9", color: e.is_billable ? "#166534" : "#475569" }}>{e.is_billable ? "Billable" : "Non-bill"}</span>
            </td>
            <td style={num}>{Number(e.hours || 0).toFixed(2)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
